import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Grid, Button } from "@mui/material";
import LoaderWrapper from "../../../wrapper/LoaderWrapper";
import { useAsync, useAsyncFn } from "../../../../hooks/useAsync";
import usePaginationWithSearchParams from "../../../../hooks/usePaginationWithSearchParams";
import useFilterSearchParams from "../../../../hooks/useFilterSearchParams";
import useCorrespondenceService from "../../../../services/correspondenceService";
import UniversalToolBarWithDialogs from "../../../bar/UniversalToolBarWithDialogs";
import TableService from "../../../../services/tableService";
import { exportMyPostInListToFileUrl, exportPostInListToFileUrl } from "../../../../helpers/apiUrls";
import {
  getFieldValueFromSearchParams,
  isSearchParamsObjectEmpty,
} from "../../../../helpers/methods";

import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import {
  centerVericalAlignIconStyle,
  overflowButtonStyle,
} from "../../../../helpers/styles";

import {
  POST_TABLE_NAME,
  POST_TABLE_FILTER_PREFIX,
  POST_IN_TABLE_CONFIG,
} from "./TableConfig";
import CorrespondenceTable from "../../../table/CorrespondenceTable";
import CreatePostDialog from "../../../dialog/CreatePostDialog/CreatePostDialog";
import { DIALOG_CREATE_MODE, DIALOG_EDIT_MODE } from "../../../../helpers/constants";
import useDialog from "../../../../hooks/useDialog";
import DetailsDrawer from "../../../drawer/UniversalDetailsDrawer/UniversalDetailsDrawer";
import useBasicDrawer from "../../../../hooks/useBasicDrawer";

const tableConfig = {
  name: POST_TABLE_NAME,
  config: POST_IN_TABLE_CONFIG,
};

const CorrespondencePostInTab = (props) => {
  const { t } = useTranslation();

  console.log('2',props.isMyCorrespondenceView)
  const {
    page,
    pageSize,
    handleChangePageWithSearchParams,
    handelChangeSearchParams,
    handleChangeRowsPerPage,
    resetPageNumber,
    searchParams,
    setSearchParams,
  } = usePaginationWithSearchParams();

  const [
    openCreatePostDialog,
    onOpenCreatePostDialog,
    onCloseCreatePostDialog,
  ] = useDialog();

  const [isDialogWithParamsClosed, setIsDialogWithParamsClosed] = useState(true);
  const postDialogModeRef = useRef();

  const handleOpenPostDialogInCreateMode = () => {
    postDialogModeRef.current = DIALOG_CREATE_MODE;
    onOpenCreatePostDialog();
  };

  const handleOpenPostDialogInEditMode = (postId) => {
    postDialogModeRef.current = DIALOG_EDIT_MODE;
    handleOpenDetailsDrawer(postId);
  };

  const getPostDialogCallback = () => {
    if (
      postDialogModeRef.current === DIALOG_CREATE_MODE &&
      !isSearchParamsObjectEmpty(searchParams)
    ) {
      setSearchParams(
        clearSearchParamsByFilterPrefix(undefined)
      );
    } else {
      postInData.refetch();
    }
  };

  const {
    setNewItemSearchParamsIfAreChanged,
    getSearchParamsByFilterPrefix,
    clearSearchParamsByFilterPrefix,
  } = useFilterSearchParams(searchParams);


  const { getPostsInData, getMyPostsInData, getPostData } = useCorrespondenceService();
  
  const combinedParams = useMemo(() => {
    const searchParamsObj = Object.fromEntries(searchParams.entries ? searchParams.entries() : []);
    
    return {
      ...searchParamsObj,
      ...props.filterParams,
      
    };
  }, [searchParams, props.filterParams]);


    const postInData = useAsync(() => {
    if (props.isMyCorrespondenceView){
      return getMyPostsInData({ ...combinedParams})
    }
    return  getPostsInData({ ...combinedParams})}, 
    [combinedParams, props.isMyCorrespondenceView]);

  useEffect(() => {
    setNewItemSearchParamsIfAreChanged(
      undefined,
      searchParams,
      setSearchParams, 
    );
  }, [searchParams]);


  const [postInDataLocal, setPostInDataLocal] = useState(undefined);
  const [countRecords, setCountRecords] = useState(0);

  useEffect(() => {
    if (postInData.loading) {
      return;
    }
    setPostInDataLocal(postInData.value ? postInData.value.results : []);
    setCountRecords(postInData.value ? postInData.value.count : 0);
  }, [postInData.loading, postInData.value]);

  const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState({});
  const [columnsOrdersForTables, setColumnsOrdersForTables] = useState({});

  const refreshTable = () => {
    setPostInDataLocal([]);
    postInData.refetch();
    if (openDetailsDrawer && openedPostIdRef.current) {
      executeGetPostDetailsDataFn(openedPostIdRef.current);
    }
  };

  const refreshDrawerTable = () => {
    if (openDetailsDrawer && openedPostIdRef.current) {
      executeGetPostDetailsDataFn(openedPostIdRef.current);
    }
  };

  const onCleanFiltersInFilterDialog = () => {
    setPostInDataLocal(undefined);
    setCountRecords(0);
    props.filtersForm({});
  };

  const [openDetailsDrawer, setOpenDetailsDrawer] = useBasicDrawer(false);
  const openedPostIdRef = useRef();
  const opendedPostDetailsDataRef = useRef();
  const getPostDataFn = useAsyncFn(getPostData);

  const handleOpenDetailsDrawer = useCallback(
    (postId) => {
      if (
        openDetailsDrawer &&
        (!postId || postId === opendedPostDetailsDataRef.current?.id)
      ) {
        setOpenDetailsDrawer(false);
        openedPostIdRef.current = null;
        opendedPostDetailsDataRef.current = null;
      } else {
        setOpenDetailsDrawer(true);
        openedPostIdRef.current = postId;
        executeGetPostDetailsDataFn(postId);
      }
    },
    [openDetailsDrawer, openedPostIdRef.current]
  );

  const executeGetPostDetailsDataFn = (postId) => {
    getPostDataFn.execute(postId).then(
      (data) => (opendedPostDetailsDataRef.current = data)
    );
  };

  const postExportHeaders = useMemo(() => {
    if (!hiddenColumnsForTables || !columnsOrdersForTables) return [];
    return TableService.getTableExportHeaders(
      POST_IN_TABLE_CONFIG,
      hiddenColumnsForTables?.[POST_TABLE_NAME],
      columnsOrdersForTables?.[POST_TABLE_NAME]
    );
  }, [
    hiddenColumnsForTables?.[POST_TABLE_NAME],
    columnsOrdersForTables?.[POST_TABLE_NAME],
    POST_IN_TABLE_CONFIG,
  ]);

  const isLoading = postInDataLocal === undefined || props.filteringData.loading 
  
  return (
    <DetailsDrawer
      openDrawer={openDetailsDrawer}
      setOpenDrawer={handleOpenDetailsDrawer}
      filteringData={props.filteringData}
      backdropColor={"rgba(0, 128, 0, 0.5)"}
      itemData={opendedPostDetailsDataRef.current}
      itemType={"post"}
      readOnly={true}
      isLoading={getPostDataFn.loading || !opendedPostDetailsDataRef.current}
      onOpenDialogParamsAction={setIsDialogWithParamsClosed}
      onRefetchData={refreshTable}
      onRefetchTableData={refreshDrawerTable}
      hasEditPermission={props.hasEditPermission}
      availableActionButtons={props.hasEditPermission ? ["enclosures", "copy_post", "edit_post", "delete"] : ["enclosures"]}
    >
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <UniversalToolBarWithDialogs
            pageName={props.pageName}
            showOpenCloseFilterButton={false}
            tableConfig={tableConfig}
            filteringData={props.filteringData}
            refreshTable={refreshTable}
            onCleanFlitersInFilterDialog={onCleanFiltersInFilterDialog}
            resetPageNumber={resetPageNumber}
            hiddenColumnsForTables={hiddenColumnsForTables}
            setHiddenColumnsForTables={setHiddenColumnsForTables}
            columnsOrdersForTables={columnsOrdersForTables}
            setColumnsOrdersForTables={setColumnsOrdersForTables}
            // filterPrefix={POST_TABLE_FILTER_PREFIX}
            extraButtonList={
              props.hasEditPermission
                ? [
                  <Button
                    key="create-post-button"
                    color="success"
                    onClick={handleOpenPostDialogInCreateMode}
                    variant="contained"
                    size="small"
                    fullWidth
                    startIcon={
                      <ControlPointOutlinedIcon
                        sx={centerVericalAlignIconStyle}
                      />
                    }
                    sx={overflowButtonStyle}
                  >
                    {t("bar.tool_bar.create_post_in")}
                  </Button>,
                ]
                : []
            }
          />
        </Grid>
        <Grid item xs={12}>
          {hiddenColumnsForTables && (
            <LoaderWrapper showLoader={isLoading} >
              <CorrespondenceTable
                showCleanFilterIcon={true}
                style={{ maxHeight: "60vh" }}
                showFilters={true}
                data={postInDataLocal}
                filteringData={props.filteringData}
                // filterPrefix={POST_TABLE_FILTER_PREFIX}
                countRecords={countRecords}
                page={page}
                onPageChange={handleChangePageWithSearchParams}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                onClickRow={handleOpenPostDialogInEditMode}
                selectedPostId={openedPostIdRef.current}
                resetPageNumber={resetPageNumber}
                tableConfig={POST_IN_TABLE_CONFIG}
                hiddenColumns={hiddenColumnsForTables[POST_TABLE_NAME]}
                columnsOrders={columnsOrdersForTables[POST_TABLE_NAME]}
                showExportToFileButton={true}
                exportToFileUrl={props.isMyCorrespondenceView ? exportMyPostInListToFileUrl : exportPostInListToFileUrl}
                exportToFileSearchParams={{
                  ...combinedParams,
                }}
                exportToFileHeaders={postExportHeaders}
              exportToFileFileName={props.isMyCorrespondenceView ? `${t("page.correspondence_page.my_post_in")}.xlsx` :`${t("page.correspondence_page.post_in")}.xlsx`}
                isLoading={postInData.loading || isLoading}
              />
            </LoaderWrapper>
          )}
        </Grid>
        <Grid item xs={12}>
          {openCreatePostDialog && (
            <CreatePostDialog
              open={openCreatePostDialog}
              onClose={onCloseCreatePostDialog}
              onSubmitCallback={getPostDialogCallback}
              dialogMode={postDialogModeRef.current}
              onRefetchData={refreshTable}
              postIn={true}
              autocompleteData={props.filteringData.value}
              backdropColor={"rgba(0, 128, 0, 0.5)"}
            />
          )}
        </Grid>
      </Grid>
    </DetailsDrawer>
  );
};

CorrespondencePostInTab.propTypes = {
  pageName: PropTypes.string,
  readOnly: PropTypes.bool,
};

CorrespondencePostInTab.defaultProps = {};

export default CorrespondencePostInTab;
